import { FC, useMemo } from 'react'

import RFFFields from '../../react-final-form-fields'
import HorizontalRule from '../../../horizontal-rule'
import Badge from '../../../badge/badge'

import { getPlaceholderTextFromLabel } from '../../../../util'
import { validateRequired, validateArrayRequired } from '../../../../util/validation'

import { useTrainingPeopleDetailsQuery, useTrainingTypesQuery } from '../../../../services'

import { ITrainingEventFormProps } from './training-event-form-types'

const titleLabel = 'Title'
const typeLabel = 'Training Type(s)'
const dateLabel = 'Date'
const instructorNameLabel = 'Instructor Name'
const isGroupTrainingLabel = 'Is Group Training?'
const notesLabel = 'Notes'
const attendeesLabel = 'Attendees'

const TrainingEventForm: FC<ITrainingEventFormProps> = ({
  handleSubmit,
  submitting,
  formName,
  disabled,
  showAttendeesOnly,
  initialValues,
  ...rest
}) => {
  const { data: trainingTypes } = useTrainingTypesQuery({ activeOnly: true })
  const typeOptions = useMemo(() => {
    if (!trainingTypes) return []
    const groups: {
      [key: string]: { label: string; options: { label: string; value: number; parentLabel: string }[] }
    } = {}
    trainingTypes.forEach(curr => {
      const category = curr.trainingCategory?.displayName
      const groupLabel = category || curr.displayName
      if (!groups[groupLabel]) {
        groups[groupLabel] = { label: groupLabel, options: [] }
      }
      groups[groupLabel].options.push({
        label: curr.displayName,
        value: curr.id,
        parentLabel: groupLabel,
      })
    })
    return Object.values(groups)
  }, [trainingTypes])

  // Fetch everyone, not just active people; an inactive person who was previously
  // added as an attendee still needs to render in the selected pane
  const { data: trainingPeople } = useTrainingPeopleDetailsQuery({ activeOnly: false })

  // Separated people are filtered out of peopleOptions below unless they were already
  // an attendee of this event, so we need to know who that was before any edits
  const initialAttendeeIds = useMemo(() => new Set(initialValues?.attendees ?? []), [initialValues])

  const peopleOptions = useMemo(
    () =>
      trainingPeople
        ?.filter(person => person.isActive || initialAttendeeIds.has(person.id))
        .map(person => ({
          label: person.displayName,
          value: person.id,
          parents:
            person.trainingGroups
              ?.slice()
              .sort((a, b) => a.displayName.localeCompare(b.displayName))
              .map(group => ({
                label: group.displayName,
                value: group.id,
              })) ?? [],
        })) ?? [],
    [trainingPeople, initialAttendeeIds],
  )
  const hasParentGroups = useMemo(
    () => peopleOptions.some(option => option.parents && option.parents.length > 0),
    [peopleOptions],
  )

  const renderAttendeeBadge = (optionValue: number) => {
    const person = trainingPeople?.find(p => p.id === optionValue)
    return person && !person.isActive ? <Badge status="Separated" color="#B23B3B" marginNone /> : null
  }

  return (
    <form onSubmit={handleSubmit} id={formName}>
      {!showAttendeesOnly && (
        <>
          <RFFFields.Input
            name="title"
            label={titleLabel}
            placeholder={getPlaceholderTextFromLabel(titleLabel)}
            validate={validateRequired}
            disabled={submitting}
          />
          <RFFFields.DatePicker
            name="date"
            label={dateLabel}
            placeholderText={getPlaceholderTextFromLabel(dateLabel)}
            disabled={submitting}
            validate={validateRequired}
            popperPlacement="bottom-start"
            isDateOnly
            shouldCloseOnSelect
            hidden={showAttendeesOnly}
          />
          <RFFFields.Select
            name="trainingTypeIds"
            label={typeLabel}
            isMultiCheckboxTree
            options={typeOptions}
            disabled={disabled}
            validate={validateArrayRequired}
          />
          <RFFFields.Input
            name="instructorName"
            label={instructorNameLabel}
            placeholder={getPlaceholderTextFromLabel(instructorNameLabel)}
            disabled={submitting}
          />
          <RFFFields.Switch
            name="isGroupTraining"
            label={isGroupTrainingLabel}
            onLabel="yes"
            offLabel="no"
            disabled={submitting}
          />
          <RFFFields.Textarea
            name="notes"
            label={notesLabel}
            placeholder={getPlaceholderTextFromLabel(notesLabel)}
            disabled={submitting}
          />
        </>
      )}

      <RFFFields.DualList
        name="attendees"
        label={attendeesLabel}
        unselectedTitle="Available Attendees"
        selectedTitle="Selected Attendees"
        options={peopleOptions}
        disabled={submitting}
        canFilter
        filterPlaceholder="Filter by attendee or group"
        displayParentGroups={hasParentGroups}
        extraOptions={renderAttendeeBadge}
      />

      {!showAttendeesOnly && (
        <>
          <HorizontalRule />

          <RFFFields.DocumentUpload
            wrapperClassName="mb-0"
            name="documents"
            disabled={disabled}
            parentType="TrainingEvent"
          />
        </>
      )}
    </form>
  )
}

export default TrainingEventForm
